import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import colors from "styles/colors";
import { Check2 } from "styled-icons/bootstrap";
import rcrmSelectors from "modules/rcrm/selectors";
import Row from "components/Row";
import Column from "components/Column";
import Icon from "components/Icon";
import rcrmActions from "modules/rcrm/actions";
import TitleLabel from "components/TitleLabel";

const CRMSettingContent = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const crmLeadTarget = useSelector((state) => rcrmSelectors.getLeadTargetResult(state));
  const rcrmGateItem = useSelector((state) => rcrmSelectors.setGateItemResult(state));
  const rcrmLoading = useSelector((state) => rcrmSelectors.setCRMLoadingResult(state));
  const validateGate = useSelector((state) => rcrmSelectors.setValidateGateResult(state));
  const [isOptionsVisible, setOptionsVisible] = useState(false);

  const GateContainer = () => {
    const handleOnSelected = (item, index) => {
      dispatch(rcrmActions.setGateItem({ gateId: item.gate_id, gateName: item.gate_name }));
      dispatch(rcrmActions.setValidateGate({ result: false }));
    };
    const onDropdownButtonClicked = () => {
      setOptionsVisible(!isOptionsVisible);
      dispatch(rcrmActions.setValidateGate({ result: false }));
    };
    const onDropdownItemSelected = (item, index) => {
      onDropdownButtonClicked();
      handleOnSelected(item, index);
    };

    const DropdownItem = ({ item, index, onClick }) => {
      return (
        <StyledItem onClick={() => onClick(item, index)}>
          <GateContent
            haveChecked={
              rcrmGateItem.data.gateItem.gateId == item.gate_id && rcrmGateItem.data.gateItem.gateName == item.gate_name
            }
          >
            <div className="gate-name">{item.gate_name}</div>
            <div className="gate-id">{item.gate_id}</div>
          </GateContent>
          {rcrmGateItem.data.gateItem.gateId == item.gate_id &&
            rcrmGateItem.data.gateItem.gateName == item.gate_name && <CheckIcon color="#555" />}
        </StyledItem>
      );
    };
    return (
      <Container>
        {Object.keys(rcrmGateItem.data.gateItem).length !== 0 && !rcrmLoading.data.loading ? (
          <>
            <TitleLabel title={t("__gate_label")} required={true} />
            <DropdownButton
              className={`dropdown-button`}
              alignItems="center"
              columnSpacing="8px"
              minWidth="100%"
              onClick={onDropdownButtonClicked}
              onSelected={handleOnSelected}
              errorStatus={
                validateGate.data.validateGate.result && validateGate.data.validateGate.msg == "gate_not_found"
              }
            >
              <Column width="100%">
                {crmLeadTarget.result === 0 && crmLeadTarget.msg === "GET_DATA_COMPLETED" && (
                  <GateContent>
                    <div className="gate-name">{rcrmGateItem.data.gateItem.gateName}</div>
                    <div className="gate-id">{rcrmGateItem.data.gateItem.gateId}</div>
                  </GateContent>
                )}
              </Column>
              <Column wrap>
                <Icon name="icon-down-dir" size="14px" color="#555"></Icon>
              </Column>
            </DropdownButton>
            <ItemsContainer isVisible={isOptionsVisible} position={{ top: "102px" }} minWidth="100%">
              {crmLeadTarget.result === 0 &&
                crmLeadTarget.msg === "GET_DATA_COMPLETED" &&
                crmLeadTarget.gates &&
                crmLeadTarget.gates.map((item, index) => (
                  <DropdownItem key={index} item={item} index={index} onClick={onDropdownItemSelected}></DropdownItem>
                ))}
            </ItemsContainer>
            {isOptionsVisible && <Backdrop onClick={() => setOptionsVisible(false)} />}
            {validateGate.data.validateGate.result && validateGate.data.validateGate.msg == "gate_not_found" && (
              <TextWarning>{t("__rcrm_error_gate_lost")}</TextWarning>
            )}
          </>
        ) : rcrmLoading.data.loading ? (
          <LoadingArea>
            <StyledLoadingIcon></StyledLoadingIcon>
          </LoadingArea>
        ) : null}
      </Container>
    );
  };

  return <>{GateContainer()}</>;
};

export default CRMSettingContent;

const Container = styled.div`
  position: relative;

  > *:first-child {
    cursor: pointer;
  }

  .custom-dropdown-button {
    display: flex;
    align-items: center;

    > * {
      min-width: 0;
    }
  }
`;

const DropdownButton = styled(Row).attrs((props) => ({
  minWidth: props.minWidth,
  opacity: props.disabled ? 0.75 : 1,
  errorStatus: props.errorStatus,
}))`
  border: 1px solid ${(props) => (props.errorStatus ? colors.negative : "#f0f0f0")};
  padding: 4px 8px 4px 16px;
  border-radius: 8px;
  box-sizing: border-box;
  min-width: ${(props) => props.minWidth};
  opacity: ${(props) => props.opacity};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")} !important;
  background-color: #fff;

  &.no-border {
    border: none;
    background-color: transparent;
  }

  &.no-border .column > p {
    font-size: 1.2rem;
  }
`;

const ItemsContainer = styled.div.attrs((props) => ({
  display: props.isVisible ? "block" : "none",
  top: props.position ? props.position.top : "35px",
  right: props.position && props.position.right ? props.position.right : null,
  minWidth: props.minWidth,
}))`
  display: ${(props) => props.display};
  position: absolute;
  border: 1px solid #f0f0f0;
  background-color: ${colors.backgroundSection};
  box-shadow: ${colors.sectionShadow} 0 10px 30px;
  min-width: 150px;
  border-radius: 8px;
  z-index: 999;
  top: ${(props) => props.top};
  right: ${(props) => props.right};
  min-width: ${(props) => props.minWidth};
  padding: 16px 0;
  text-align: left;
  max-height: 300px;
  overflow: auto;

  @media screen and (max-width: 834px) {
    box-shadow: none;
    width: calc(100% - 32px);
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    margin: 16px;
    text-align: center;
    max-height: calc(100vh - 150px);
    min-width: unset;

    p {
      font-size: 14px;
    }
  }
`;

const StyledItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: 834px) {
    padding: 16px;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 998;

  @media screen and (max-width: 834px) {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const GateContent = styled.div.attrs((props) => ({
  marginLeft: props.haveChecked ? "20px" : "",
}))`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  line-height: 1.5rem;
  font-size: 14px;
  width: 100%;
  .gate-name {
    line-height: 1.2rem;
    color: #555;
    width: 100%;
    white-space: wrap;
    word-break: break-word;
  }
  .gate-id {
    width: 100%;
    color: #aaa;
    font-size: 12px;
  }
  @media screen and (max-width: 834px) {
    justify-content: center;
    margin-left: ${(props) => props.marginLeft};
  }
`;

const LoadingArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  height: 180px;
`;

const StyledLoadingIcon = styled.div`
  border: 5px solid #ccc;
  border-top: 5px solid #eee;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;
  position: sticky;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const CheckIcon = styled(Check2).attrs((props) => ({
  color: props.color,
}))`
  color: ${(props) => props.color};
  width: 20px;
  height: 20px;
`;

const TextWarning = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  color: ${colors.negative};
  font-size: 12px;
  font-weight: normal;
`;
